import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { STYLES, ASPECT_RATIOS, Style, AspectRatio, IdeogramPrompt } from './types';
import { generateImageAsync } from '../../services/ideogramService';
import { toast } from '../ui/use-toast';
import { Loader2, Wand2 } from 'lucide-react';

interface RemixDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originalImage: IdeogramPrompt;
  onStartGenerating: () => void;
  onCloseImageDetails: () => void;
}

export const RemixDialog: React.FC<RemixDialogProps> = ({
  open,
  onOpenChange,
  originalImage,
  onStartGenerating,
  onCloseImageDetails,
}) => {
  const [prompt, setPrompt] = useState(originalImage.prompt);
  const [style, setStyle] = useState<Style>(originalImage.style);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(originalImage.aspectRatio);
  const [imageWeight, setImageWeight] = useState(50);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      await generateImageAsync(prompt, aspectRatio, style, {
        sourceImageUrl: originalImage.imageUrl,
        imageWeight: imageWeight
      });
      
      onOpenChange(false);
      onCloseImageDetails();
      onStartGenerating();
      
      toast({
        title: "Success",
        description: "Image generation started. Please wait while we process your request.",
      });
    } catch (error) {
      console.error('Error generating image:', error);
      toast({
        title: "Error",
        description: "Failed to generate image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Remix Image</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Original Image */}
          <div>
            <img
              src={originalImage.imageUrl}
              alt={originalImage.prompt}
              className="w-full h-auto rounded-lg"
            />
          </div>
          
          {/* Remix Controls */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Prompt</label>
              <Textarea
                placeholder="Enter your prompt..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Style</label>
              <Select value={style} onValueChange={(value) => setStyle(value as Style)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select style" />
                </SelectTrigger>
                <SelectContent>
                  {STYLES.map((style) => (
                    <SelectItem key={style} value={style}>
                      {style}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Aspect Ratio</label>
              <Select value={aspectRatio} onValueChange={(value) => setAspectRatio(value as AspectRatio)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select aspect ratio" />
                </SelectTrigger>
                <SelectContent>
                  {ASPECT_RATIOS.map((ratio) => (
                    <SelectItem key={ratio} value={ratio}>
                      {ratio.replace('ASPECT_', '').replace('_', ':')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Image Weight: {imageWeight}%</label>
              <input
                type="range"
                min="0"
                max="100"
                value={imageWeight}
                onChange={(e) => setImageWeight(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Remixing...
              </>
            ) : (
              <>
                <Wand2 className="mr-2 h-4 w-4" />
                Remix Image
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
